import { createSignal, createEffect, onMount, untrack, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import Text from '../components/Text';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatTime } from '../services/dateFormat';
import type { ReconnectWindowDto } from '../api/generated/schemas/reconnectWindowDto';
import type { ReconnectWindowStatusDto } from '../api/generated/schemas/reconnectWindowStatusDto';
import styles from './Voting.module.css';
import layout from './AdminReconnectWindows.module.css';
import { firstError } from '../api/envelope';

const POLL_MS = 15_000;
const MINUTES_PER_DAY = 24 * 60;

interface WindowForm {
    displayName: string;
    startTime: string;
    durationMinutes: number;
    releaseJitterMinutes: number;
}

const emptyForm = (): WindowForm => ({
    displayName: '', startTime: '04:00', durationMinutes: 30, releaseJitterMinutes: 10,
});

const toMinuteOfDay = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return ((h * 60 + m) % MINUTES_PER_DAY + MINUTES_PER_DAY) % MINUTES_PER_DAY;
};

const toHhMm = (minuteOfDay: number): string => {
    const m = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

export default function AdminReconnectWindows() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const isRoot = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf('Root')];

    const [windows, setWindows] = createStore<ReconnectWindowDto[]>([]);
    const [status, setStatus] = createSignal<ReconnectWindowStatusDto | null>(null);
    const [loading, setLoading] = createSignal(true);

    const [formOpen, setFormOpen] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [form, setForm] = createStore<WindowForm>(emptyForm());
    const [submitting, setSubmitting] = createSignal(false);

    const fetchAll = async () => {
        if (!isRoot()) { if (loading()) setLoading(false); return; }

        const [listRes, statusRes] = await Promise.all([
            api.reconnectWindowsAdmin.listWindows(),
            api.reconnectWindowsAdmin.getStatus(),
        ]);

        if (isOk(listRes.data)) setWindows(reconcile((listRes.data.value.windows ?? []) as ReconnectWindowDto[], { key: 'id' }));
        if (isOk(statusRes.data)) setStatus(statusRes.data.value as ReconnectWindowStatusDto);
        if (loading()) setLoading(false);
    };

    const [rootFetchDone, setRootFetchDone] = createSignal(false);

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    createEffect(() => {
        if (isRoot() && !rootFetchDone()) {
            setRootFetchDone(true);
            untrack(() => void fetchAll());
        }
    });

    const endTimeLabel = (startMinuteOfDay: number, durationMinutes: number) =>
        toHhMm(startMinuteOfDay + durationMinutes);

    const openCreate = () => { setEditId(null); setForm(emptyForm()); setFormOpen(true); };
    const openEdit = (w: ReconnectWindowDto) => {
        setEditId(w.id);
        setForm({
            displayName: w.displayName,
            startTime: toHhMm(w.startMinuteOfDay),
            durationMinutes: w.durationMinutes,
            releaseJitterMinutes: w.releaseJitterMinutes,
        });
        setFormOpen(true);
    };

    const submitForm = async () => {
        setSubmitting(true);
        const body = {
            displayName: form.displayName.trim(),
            startMinuteOfDay: toMinuteOfDay(form.startTime),
            durationMinutes: Number(form.durationMinutes),
            releaseJitterMinutes: Number(form.releaseJitterMinutes),
            isEnabled: true,
        };
        const id = editId();
        const res = id
            ? await api.reconnectWindowsAdmin.updateWindow(id, { ...body, isEnabled: windows.find(w => w.id === id)?.isEnabled ?? true })
            : await api.reconnectWindowsAdmin.createWindow(body);
        setSubmitting(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('RECONNECT_WINDOW_SAVE_FAILED')); return; }
        toast?.success(id ? t('RECONNECT_WINDOW_UPDATED') : t('RECONNECT_WINDOW_CREATED'));
        setFormOpen(false);
        void fetchAll();
    };

    const toggleWindow = async (w: ReconnectWindowDto, enabled: boolean) => {
        const res = await api.reconnectWindowsAdmin.updateWindow(w.id, {
            displayName: w.displayName,
            startMinuteOfDay: w.startMinuteOfDay,
            durationMinutes: w.durationMinutes,
            releaseJitterMinutes: w.releaseJitterMinutes,
            isEnabled: enabled,
        });
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('RECONNECT_WINDOW_SAVE_FAILED')); return; }
        toast?.info(enabled ? t('RECONNECT_WINDOW_ENABLED') : t('RECONNECT_WINDOW_DISABLED'));
        void fetchAll();
    };

    const deleteWindow = async (w: ReconnectWindowDto) => {
        const ok = await showConfirm(t('RECONNECT_WINDOW_DELETE_CONFIRM', w.displayName), true);
        if (!ok) return;
        const res = await api.reconnectWindowsAdmin.deleteWindow(w.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('RECONNECT_WINDOW_DELETE_FAILED')); return; }
        toast?.success(t('RECONNECT_WINDOW_DELETED'));
        void fetchAll();
    };

    return (
        <div class={layout.container}>
            <div class={styles.headerRow}>
                <h1><Text val="ADMIN_RECONNECT_WINDOWS_TITLE" /></h1>
                <Show when={isRoot()}>
                    <span class={styles.entryCount}>{windows.length} <Text val="RECONNECT_WINDOWS_COUNT" /></span>
                </Show>
            </div>

            <Show when={isRoot()} fallback={
                <div class="fcard v" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-2xl)' }}>
                    <i class="fa-solid fa-lock" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                    <p style={{ color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOWS_ROOT_ONLY" /></p>
                </div>
            }>
                <p class={layout.hint}><Text val="RECONNECT_WINDOWS_INTRO" /></p>

                <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                    <div class={`fcard ${layout.statusCard}`}>
                        <i class={`fa-solid ${status()?.isBlocked ? `fa-pause ${layout.held}` : `fa-play ${layout.clear}`} ${layout.statusIcon}`} />
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <strong>
                                <Text val={status()?.isBlocked ? 'RECONNECT_WINDOWS_STATUS_HELD' : 'RECONNECT_WINDOWS_STATUS_CLEAR'} />
                            </strong>
                            <Show when={status()?.isBlocked}>
                                <span class={layout.hint}>
                                    {t('RECONNECT_WINDOWS_STATUS_UNTIL', status()?.windowName ?? '', formatTime(status()?.endsAtUtc ?? ''))}
                                </span>
                            </Show>
                        </div>
                    </div>
                </Show>

                <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between' }}>
                    <h2 style={{ margin: 0 }}><Text val="RECONNECT_WINDOWS_HEADER" /></h2>
                    <button class="btn btn-primary btn-sm" onClick={openCreate}>
                        <i class="fa-solid fa-plus" /> <Text val="RECONNECT_WINDOW_ADD" />
                    </button>
                </div>

                <Show when={formOpen()}>
                    <form class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-top': 'var(--spacing-md)', 'max-width': '38rem' }}
                        onSubmit={(e) => { e.preventDefault(); void submitForm(); }}>
                        <h3 style={{ margin: 0 }}><Text val={editId() ? 'RECONNECT_WINDOW_EDIT' : 'RECONNECT_WINDOW_ADD'} /></h3>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOW_FIELD_NAME" /></span>
                            <input type="text" placeholder={t('RECONNECT_WINDOW_NAME_PLACEHOLDER')} value={form.displayName}
                                onInput={(e) => setForm('displayName', e.currentTarget.value)} />
                        </label>

                        <div class="h" style={{ gap: 'var(--spacing-md)' }}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOW_FIELD_START" /></span>
                                <input type="time" value={form.startTime} onInput={(e) => setForm('startTime', e.currentTarget.value)} />
                                <span class={layout.zoneNote}><Text val="RECONNECT_WINDOW_TIMEZONE_NOTE" /></span>
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOW_FIELD_DURATION" /></span>
                                <input type="number" min="1" max="1439" value={form.durationMinutes}
                                    onInput={(e) => setForm('durationMinutes', Number(e.currentTarget.value))} />
                                <span class={layout.zoneNote}>
                                    {t('RECONNECT_WINDOW_ENDS_AT', endTimeLabel(toMinuteOfDay(form.startTime), Number(form.durationMinutes) || 0))}
                                </span>
                            </label>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOW_FIELD_JITTER" /></span>
                            <input type="number" min="0" value={form.releaseJitterMinutes}
                                onInput={(e) => setForm('releaseJitterMinutes', Number(e.currentTarget.value))} />
                            <span class={layout.zoneNote}><Text val="RECONNECT_WINDOW_JITTER_HINT" /></span>
                        </label>

                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => setFormOpen(false)}><Text val="CANCEL" /></button>
                            <button type="submit" class="btn btn-primary" disabled={submitting()}>
                                <Show when={submitting()} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                    <Show when={windows.length > 0} fallback={
                        <div class="fcard v" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-2xl)' }}>
                            <i class="fa-solid fa-clock" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                            <p style={{ color: 'var(--dark-text-muted)' }}><Text val="RECONNECT_WINDOWS_EMPTY" /></p>
                        </div>
                    }>
                        <div class="fcard" style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <div class={styles.tableWrap}>
                                <table class={styles.logTable}>
                                    <thead>
                                        <tr>
                                            <th><Text val="RECONNECT_WINDOW_COL_NAME" /></th>
                                            <th><Text val="RECONNECT_WINDOW_COL_WHEN" /></th>
                                            <th><Text val="RECONNECT_WINDOW_COL_DURATION" /></th>
                                            <th><Text val="RECONNECT_WINDOW_COL_JITTER" /></th>
                                            <th><Text val="RECONNECT_WINDOW_COL_STATE" /></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={windows}>{(w) => (
                                            <tr>
                                                <td>{w.displayName}</td>
                                                <td style={{ 'font-family': 'var(--font-family-mono)' }}>
                                                    {toHhMm(w.startMinuteOfDay)} - {endTimeLabel(w.startMinuteOfDay, w.durationMinutes)}
                                                </td>
                                                <td>{t('RECONNECT_WINDOW_MINUTES', String(w.durationMinutes))}</td>
                                                <td>{t('RECONNECT_WINDOW_MINUTES', String(w.releaseJitterMinutes))}</td>
                                                <td>
                                                    <span class={`${styles.outcomeBadge} ${w.isEnabled ? styles.outcomeOk : styles.outcomeBad}`}>
                                                        <Text val={w.isEnabled ? 'RECONNECT_WINDOW_STATE_ON' : 'RECONNECT_WINDOW_STATE_OFF'} />
                                                    </span>
                                                </td>
                                                <td>
                                                    <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                                        <button class="btn btn-secondary btn-sm"
                                                            title={t(w.isEnabled ? 'RECONNECT_WINDOW_DISABLE' : 'RECONNECT_WINDOW_ENABLE')}
                                                            onClick={() => void toggleWindow(w, !w.isEnabled)}>
                                                            <i class={`fa-solid ${w.isEnabled ? 'fa-pause' : 'fa-play'}`} />
                                                        </button>
                                                        <button class="btn btn-secondary btn-sm" title={t('EDIT')} onClick={() => openEdit(w)}>
                                                            <i class="fa-solid fa-pen-to-square" />
                                                        </button>
                                                        <button class="btn btn-danger btn-sm" title={t('DELETE')} onClick={() => void deleteWindow(w)}>
                                                            <i class="fa-solid fa-trash" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}</For>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
